import type { PaypercutBillingDetails } from '../types/elements';
import { containsAsciiControlCharacter, isRecord } from './protocol-validation';

const BILLING_DETAIL_KEYS = new Set(['name', 'email', 'phone', 'address']);
const BILLING_ADDRESS_KEYS = new Set(['line1', 'line2', 'city', 'state', 'postal_code', 'country']);

const LIMITS = {
  name: 200,
  email: 320,
  phone: 64,
  address: 256,
  postalCode: 64,
} as const;
const COUNTRY_CODE_CHARACTERS = 2;

const normalizeText = (value: unknown, field: string, maximum: number): string => {
  if (typeof value !== 'string') {
    throw new Error(`[Paypercut Elements] Billing ${field} must be a string.`);
  }
  const normalized = value.trim();
  if (
    normalized.length === 0 ||
    normalized.length > maximum ||
    containsAsciiControlCharacter(normalized)
  ) {
    throw new Error(`[Paypercut Elements] Billing ${field} is invalid.`);
  }
  return normalized;
};

export function normalizeBillingDetails(value: unknown): PaypercutBillingDetails | undefined {
  if (value === undefined) return undefined;
  if (
    !isRecord(value) ||
    Object.keys(value).length === 0 ||
    Object.keys(value).some((key) => !BILLING_DETAIL_KEYS.has(key))
  ) {
    throw new Error(
      '[Paypercut Elements] billing_details must contain only name, email, phone, or address.',
    );
  }

  let address: PaypercutBillingDetails['address'];
  if (value.address !== undefined) {
    if (
      !isRecord(value.address) ||
      Object.keys(value.address).length === 0 ||
      Object.keys(value.address).some((key) => !BILLING_ADDRESS_KEYS.has(key))
    ) {
      throw new Error(
        '[Paypercut Elements] Billing address must contain a supported address field.',
      );
    }
    const input = value.address;
    const country =
      input.country === undefined
        ? undefined
        : normalizeText(input.country, 'address country', COUNTRY_CODE_CHARACTERS).toUpperCase();
    if (country !== undefined && !/^[A-Z]{2}$/.test(country)) {
      throw new Error('[Paypercut Elements] Billing address country must be ISO 3166-1 alpha-2.');
    }
    address = Object.freeze({
      ...(input.line1 === undefined
        ? {}
        : { line1: normalizeText(input.line1, 'address line1', LIMITS.address) }),
      ...(input.line2 === undefined
        ? {}
        : { line2: normalizeText(input.line2, 'address line2', LIMITS.address) }),
      ...(input.city === undefined
        ? {}
        : { city: normalizeText(input.city, 'address city', LIMITS.address) }),
      ...(input.state === undefined
        ? {}
        : { state: normalizeText(input.state, 'address state', LIMITS.address) }),
      ...(input.postal_code === undefined
        ? {}
        : {
            postal_code: normalizeText(input.postal_code, 'address postal_code', LIMITS.postalCode),
          }),
      ...(country === undefined ? {} : { country }),
    });
  }

  return Object.freeze({
    ...(value.name === undefined ? {} : { name: normalizeText(value.name, 'name', LIMITS.name) }),
    ...(value.email === undefined
      ? {}
      : { email: normalizeText(value.email, 'email', LIMITS.email) }),
    ...(value.phone === undefined
      ? {}
      : { phone: normalizeText(value.phone, 'phone', LIMITS.phone) }),
    ...(address === undefined ? {} : { address }),
  });
}

export function billingDetailsEqual(
  left: PaypercutBillingDetails | undefined,
  right: PaypercutBillingDetails | undefined,
): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  return (
    left.name === right.name &&
    left.email === right.email &&
    left.phone === right.phone &&
    left.address?.line1 === right.address?.line1 &&
    left.address?.line2 === right.address?.line2 &&
    left.address?.city === right.address?.city &&
    left.address?.state === right.address?.state &&
    left.address?.postal_code === right.address?.postal_code &&
    left.address?.country === right.address?.country
  );
}
